import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import {
  Box,
  Button,
  Callout,
  Dialog,
  Illustration,
  Input2FA,
  toast,
} from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { ERROR_STATUS_CODES, TWO_FACTOR_CODE_LENGTH } from 'constants/General'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { generateReference } from 'utils/utils'
import { EARN_REFERENCE_PREFIX } from '../../constants/const'
import { useDistributeCustomerRewards } from '../../hooks/api/useDistributeCustomerRewards'
import { useEarnTracking } from '../../hooks/useEarnTracking'
import { EarnDistributionFormData } from '../../types'
import { invalidateEarnQueries } from '../../utils/utils'

const DEFAULT_VALUES: EarnDistributionFormData = { authenticationCode: '' }

interface EarnEmbeddedDistributionDialogProps {
  open: boolean
  onClose: () => void
  productId: string
}

export const EarnEmbeddedDistributionDialog = ({
  open,
  onClose,
  productId,
}: EarnEmbeddedDistributionDialogProps) => {
  const { t } = useTranslation()
  const reactQueryClient = useQueryClient()
  const { requestError, handleRequestError, setRequestError } = useMappedErrorMessage()
  const { trackFormInitiate, trackFormCancel } = useEarnTracking()

  const {
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<EarnDistributionFormData>({
    resolver: yupFormResolver<EarnDistributionFormData>(
      yup.object().shape({
        authenticationCode: yup
          .string()
          .length(TWO_FACTOR_CODE_LENGTH, t('2faWrongLengthError'))
          .required(t('2faEmptyError')),
      })
    ),
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  })

  useEffect(() => {
    if (open) {
      reset(DEFAULT_VALUES)
      setRequestError({ show: false })
      trackFormInitiate('distribute')
    }
  }, [open, reset, setRequestError, trackFormInitiate])

  const { distributeCustomerRewards, isDistributingCustomerRewardsPending } =
    useDistributeCustomerRewards({
      onSuccess: () => {
        invalidateEarnQueries(reactQueryClient)
        toast({
          variant: 'success',
          header: t('earn.distributionDialog.successNotificationTitle'),
        })
        onClose()
      },
      onError: error => {
        handleRequestError(error, '/earn/distribution', 'distributeCustomerRewards')
      },
    })

  const isPending = isDistributingCustomerRewardsPending || isSubmitting

  const isIncorrectAuthenticationCode =
    requestError?.show && requestError?.errorCode === ERROR_STATUS_CODES.FORBIDDEN

  const distributionRequestError = {
    ...requestError,
    handledMessage: t('earn.distributionDialog.distributionFailed'),
  }

  const handleAuthenticationCodeChange = (value: string) => {
    setValue('authenticationCode', value)
  }

  const handleAuthenticationCodeInput = () => {
    clearErrors('authenticationCode')
    setRequestError({ show: false })
  }

  const onCancel = () => {
    trackFormCancel('distribute')
    onClose()
  }

  const onSubmit = (data: EarnDistributionFormData) => {
    setRequestError({ show: false })

    distributeCustomerRewards(
      productId,
      generateReference(EARN_REFERENCE_PREFIX),
      data.authenticationCode
    )
  }

  const getInput2FAError = () => {
    if (errors.authenticationCode?.message) {
      return errors.authenticationCode.message
    }

    if (isIncorrectAuthenticationCode) {
      return t('2faRejectedError')
    }

    return undefined
  }

  return (
    <Dialog
      title={t('earn.distributionDialog.title')}
      Illustration={<Illustration name="Warning" />}
      open={open}
      onClose={onCancel}
      ActionButtons={[
        <Button
          key="confirm"
          variant="primary"
          testId="distribution-confirm-button"
          loading={isPending}
          onClick={handleSubmit(onSubmit)}
        >
          {t('continue')}
        </Button>,
        <Button
          key="cancel"
          variant="secondary"
          testId="distribution-cancel-button"
          disabled={isPending}
          onClick={onCancel}
        >
          {t('cancel')}
        </Button>,
      ]}
    >
      <Box flexCol gapY={12}>
        <p className="text-body-regular text-foreground-prominent text-center">
          {t('earn.distributionDialog.description')}
        </p>
        <Callout variant="warning" testId="distribution-callout">
          {t('earn.distributionDialog.calloutMessage')}
        </Callout>
        {requestError?.show && !isIncorrectAuthenticationCode && (
          <RequestErrorCallout requestError={distributionRequestError} />
        )}
        <Input2FA
          label={t('earn.distributionDialog.authenticationCode')}
          onChange={handleAuthenticationCodeInput}
          onComplete={handleAuthenticationCodeChange}
          key={`distribution-authentication-code-${open}`}
          error={getInput2FAError()}
          testId="distribution-2fa-input"
        />
      </Box>
    </Dialog>
  )
}
